/**
 * ImpersonationBanner — global sticky red banner shown when an admin
 * has impersonated a vendor / professional via the admin panel's
 * "Impersonate" button. Renders nothing for non-impersonated sessions.
 *
 * Mounted once inside <App /> so it appears on every route.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImpersonationBanner() {
  const [status, setStatus] = useState({ is_impersonating: false });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/impersonation/status`,
        { withCredentials: true },
      );
      setStatus(data || { is_impersonating: false });
    } catch {
      setStatus({ is_impersonating: false });
    }
  };

  useEffect(() => { refresh(); }, []);

  // Re-poll on focus so the banner appears immediately after admin
  // initiates impersonation from another tab.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const stop = async (notify) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/stop-impersonate`,
        { notify: !!notify },
        { withCredentials: true },
      );
      toast.success(notify ? 'Done — partner will see "review my new content" banner on next login' : 'Returned to admin');
      setStatus({ is_impersonating: false });
      // Force a hard reload so AuthContext re-fetches as admin.
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not stop impersonation');
    } finally {
      setBusy(false);
    }
  };

  if (!status.is_impersonating) return null;

  const t = status.target || {};
  const label = t.business_name || t.name || 'partner';

  return (
    <div
      className="sticky top-0 z-[100] w-full bg-rose-700 text-sand-50 text-sm shadow-md"
      data-testid="impersonation-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-3 py-2 flex flex-wrap items-center gap-2">
        <span className="font-bold uppercase tracking-wider text-[10px] bg-sand-50 text-rose-700 px-2 py-0.5 rounded">
          Admin Impersonation
        </span>
        <span className="flex-1 min-w-0 truncate">
          You are logged in as <strong>{label}</strong>
          {t.role && (
            <span className="opacity-80 ml-1">
              ({t.role}{t.category ? ` · ${t.category}` : ''})
            </span>
          )}
          . Use this account&apos;s dashboard Quick Add to seed packages on their behalf.
        </span>
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-[11px] uppercase tracking-wider font-bold underline hover:no-underline"
            data-testid="impersonation-go-dashboard"
          >
            Go to dashboard
          </button>
          <button
            type="button"
            onClick={() => stop(true)}
            disabled={busy}
            className="text-[11px] uppercase tracking-wider font-bold bg-sand-50 text-rose-700 px-2 py-1 rounded disabled:opacity-50"
            data-testid="impersonation-done-notify-btn"
          >
            Done — Notify Partner
          </button>
          <button
            type="button"
            onClick={() => stop(false)}
            disabled={busy}
            className="text-[11px] uppercase tracking-wider font-bold border border-sand-50 px-2 py-1 rounded disabled:opacity-50"
            data-testid="impersonation-stop-btn"
          >
            Return to Admin
          </button>
        </div>
      </div>
    </div>
  );
}
